import { UnreadCountDto, UserStatsDto, validStr } from 'booksprout'
import { LOCAL_STORAGE_FORWARD_URL_KEY } from '../../store/actions/ui'
import store from './store'
import { AuthRequiredData, ShowMessageDialogData, StoreRedirectData } from './store/types'
import {
  _clearNotifications,
  _notifyError,
  _redirectTo,
  _setLastError,
  _setReadyToRender,
  _setUnreadMessageCount,
  _setUnreadNotificationCount,
  _setUserStats,
  _showLoginNotification,
  _showMessageDialog
} from './store/mutations'

/**
 * Replaces the Vuex `ui` module.
 *
 * Most of its actions were a one-line `commit` of their own name, so those collapse into the
 * mutation calls directly. Only `authRequired` does anything else, and `notifyError` /
 * `showMessageDialog` reshape their payload on the way in.
 */

export const useUi = () => {
  const notifyError = (message: string) => {
    _notifyError(message)
  }

  const clearNotifications = () => {
    _clearNotifications()
  }

  /**
   * Carried over from the Vuex action, including the `router` fork: with a router the visitor is
   * sent to /login, without one a login prompt is raised in place (the notification banner).
   *
   * authenticationService clears the session and then calls this through the handler each app's
   * bootstrap injects — the two halves used to be one dispatch that Vuex fanned out to both the auth
   * and ui modules.
   */
  const authRequired = (data?: AuthRequiredData) => {
    // Persisted for reliability across OAuth flows, page refreshes, etc.
    if (validStr(data?.forwardUrl)) {
      localStorage.setItem(LOCAL_STORAGE_FORWARD_URL_KEY, (data as AuthRequiredData).forwardUrl)
    }

    const forwardToEncoded = data ? encodeURIComponent(data.forwardUrl) : 'undefined'
    const forwardUrl = forwardToEncoded !== 'undefined' ? '/login?forward=' + forwardToEncoded : ''

    if (data?.router) {
      return data.router.push(forwardUrl)
    }

    _showLoginNotification({
      show: true,
      forwardUrl: forwardUrl
    })
  }

  const clearAuthRequired = () => {
    _showLoginNotification({
      show: false,
      forwardUrl: ''
    })
  }

  /**
   * The generic error entry point. Only ever reached with an Error-shaped value.
   */
  const errorGeneric = (error: { message: string }) => {
    _notifyError(error.message)
  }

  const setUnreadMessageCount = (count: number) => {
    _setUnreadMessageCount(count)
  }

  const setUnreadNotificationCount = (unreadCountDto: UnreadCountDto) => {
    _setUnreadNotificationCount(unreadCountDto)
  }

  const redirectTo = (redirectionData: StoreRedirectData | undefined) => {
    _redirectTo(redirectionData)
  }

  /**
   * `error` is optional because callers use this for success messages too — the Vuex action spread
   * the payload as-is, so it arrived undefined and the dialog treated it as not-an-error.
   */
  const showMessageDialog = (data: Partial<Omit<ShowMessageDialogData, 'show'>> & { message: string }) => {
    _showMessageDialog({
      ...data,
      show: true
    } as ShowMessageDialogData)
  }

  const clearMessageDialog = () => {
    _showMessageDialog({ show: false } as ShowMessageDialogData)
  }

  const setUserStats = (stats: UserStatsDto) => {
    _setUserStats(stats)
  }

  const setReadyToRender = (isReadyToRender: boolean) => {
    _setReadyToRender(isReadyToRender)
  }

  /**
   * `showError` takes `string | TranslateResult`, and the untyped dispatch let a TranslateResult
   * into the state. `$t`/`$tc` return strings in practice; normalising keeps the store's own type
   * honest either way.
   */
  const setLastError = (error: string | { toString (): string }) => {
    _setLastError(typeof error === 'string' ? error : String(error))
  }

  return {
    ui: {
      store,

      notifyError,
      clearNotifications,
      authRequired,
      clearAuthRequired,
      errorGeneric,
      setUnreadMessageCount,
      setUnreadNotificationCount,
      redirectTo,
      showMessageDialog,
      clearMessageDialog,
      setUserStats,
      setReadyToRender,
      setLastError
    }
  }
}
